import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
} from '@mui/material';
import { useSellers } from '../../../lib/query/hooks';
import { downloadCSV } from '../../../lib/utils';
import type { SimpleAccount } from '@/lib/types';

interface SellerListProps {
  account: SimpleAccount;
}

export function SellerList({ account }: SellerListProps) {
  const { data: sellers = [], isLoading: loading, error } = useSellers(account.code);

  // CSVダウンロード処理
  const handleDownloadCSV = () => {
    const csvData = sellers.map((seller) => ({
      店舗カスタマーID: seller.customer_id,
      店舗名: seller.name,
    }));

    const filename = `店舗一覧_${account.code}_${new Date().toISOString().split('T')[0]}.csv`;
    downloadCSV(csvData, filename);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>店舗一覧を読み込み中...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error instanceof Error ? error.message : '店舗一覧の読み込みに失敗しました。'}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Card>
        <CardContent>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
          >
            <Typography variant="h6">店舗一覧 ({sellers.length}件)</Typography>
            {sellers.length > 0 && (
              <Button variant="outlined" onClick={handleDownloadCSV} size="small">
                CSVでダウンロード
              </Button>
            )}
          </Box>

          {sellers.length === 0 ? (
            <Alert severity="info">店舗データが見つかりませんでした。</Alert>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                mt: 2,
                overflowX: 'auto',
                maxWidth: '100%',
                '& .MuiTable-root': {
                  minWidth: 650,
                },
              }}
            >
              <Table sx={{ whiteSpace: 'nowrap' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>店舗カスタマーID</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>店舗名</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>拠点タイプ</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>表示状態</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sellers.map((seller, index) => (
                    <TableRow key={`${seller.id}-${index}`}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{seller.customer_id}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{seller.name}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{seller.site_type || '-'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Chip
                          label={seller.is_enabled_current ? '有効' : '無効'}
                          color={seller.is_enabled_current ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
